import React, { useEffect, useMemo, useState } from 'react';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { GeneInput } from '../../utilities/GeneInput.tsx';
import { Button, InputGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMars, faVenus, faNeuter, faPlus } from '@fortawesome/free-solid-svg-icons';
import { makeMorphNameStringPotential } from '../../../utils/GeneCalculator.ts';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { Gene, blankGene, geneExpressions, GeneExpression } from '../../../types/Gene.ts';
import { Morph } from '../../../types/Morph.ts';
import { driver } from 'driver.js';
import { CollectionGroup } from '../../utilities/CollectionGroup.tsx';
import { useRepToolContext } from '../../../RepToolContext.tsx';

export const SexInputs = ({ setValues }: { setValues: Function }) => {
  const [males, setMales] = useState(0);
  const [females, setFemales] = useState(0);
  const [unsexed, setUnsexed] = useState(0);

  useEffect(() => {
    setValues({ males, females, unsexed });
  }, [males, females, unsexed])

  return (
    <div id='quantity-add-genes' className='d-flex flex-row w-100 justify-content-around'>
      <InputGroup>
        <InputGroup.Text className='male text-light gender-btn border-0'>
          <FontAwesomeIcon icon={faMars} size='xl' />
        </InputGroup.Text>
        <Form.Control
          type='number'
          className='h-100'
          min={0}
          value={males}
          onChange={(e) => setMales(parseInt(e.target.value))}
        />
      </InputGroup>
      <InputGroup className='mx-3'>
        <InputGroup.Text className='female text-light gender-btn border-0'>
          <FontAwesomeIcon icon={faVenus} size='xl' />
        </InputGroup.Text>
        <Form.Control
          type='number'
          className='h-100'
          min={0}
          value={females}
          onChange={(e) => setFemales(parseInt(e.target.value))}
        />
      </InputGroup>
      <InputGroup>
        <InputGroup.Text className='none gender-btn border-0'>
          <FontAwesomeIcon icon={faNeuter} size='xl' />
        </InputGroup.Text>
        <Form.Control
          type='number'
          className='h-100'
          min={0}
          value={unsexed}
          onChange={(e) => setUnsexed(parseInt(e.target.value))}
        />
      </InputGroup>
    </div>
  )
}


export const AddGeneModal = ({
  status,
  genes,
  closeModal,
  morphs,
  updateGroups,
  lockStatus = false,
  preselectedReptile
}: {
  status: string,
  genes: Gene[],
  closeModal: () => void,
  morphs: Morph[],
  updateGroups: () => void,
  lockStatus?: boolean,
  preselectedReptile?: string
}) => {
  const tabs = [
    { label: "Available", status: "available" },
    { label: "Not For Sale", status: "nfs" },
    { label: "Sold", status: "sold" }
  ];
  const { appendControls } = useRepToolContext();
  const [selectedGenes, setSelectedGenes] = useState<Gene[]>([blankGene]);
  const [selectedReptile, setSelectedReptile] = useState<string>(preselectedReptile ? preselectedReptile : localStorage.getItem('selectedReptile') || '');
  const [selectedStatus, setSelectedStatus] = useState(status)
  const [genderQuantities, setGenderQuantities] = useState({ males: 0, females: 0, unsexed: 0 });
  const [isNormal, setIsNormal] = useState(false);

  const morphNameString = useMemo(() => {
    return makeMorphNameStringPotential(selectedGenes, morphs);
  }, [selectedGenes, morphs])


  useEffect(() => {
    const atleastOneAnimal = genderQuantities.females + genderQuantities.males + genderQuantities.unsexed > 0;

    const formComplete = atleastOneAnimal;
    appendControls(
      <>
        <Button id='add-to-collection' onClick={save} className='mx-auto' variant={formComplete ? 'outline-light' : 'outline-secondary'}>
          Add To Collection
        </Button>
      </>, 'Add Animal By Genes'
    )
  }, [selectedGenes, genderQuantities]);


  const save = () => {
    if (!isValid()) {
      alert("Inputs not valid. Please specify how many reptiles you are adding.");
      return;
    }

    const data = {
      females: genderQuantities.females || 0,
      males: genderQuantities.males || 0,
      morphName: morphNameString,
      reptile: selectedReptile,
      status: selectedStatus,
      unknown: genderQuantities.unsexed || 0,
      genes: selectedGenes.filter(gene => gene.gene_name !== "" || gene.id)
    }

    BasicFetch({
      url: `${(window as any).serverLocation}/reptile/reptile_group/create`,
      data: data,
      method: 'POST',
      onSuccess: (data) => {
        window.alert("Successfully added reptiles to your collection.");
        closeModal();
        updateGroups();
      },
      onFail: () => {
        window.alert("There was an issue adding these reptiles. Please try again later.")
      },
    })
  }

  const isValid = () => {
    let quantity = 0;
    Object.keys(genderQuantities).forEach(gender => {
      quantity += genderQuantities[gender];
    })
    return quantity > 0
  }

  let conditionalSteps: { element: string, popover: { title: string, description: string } }[] = [];
  if (!lockStatus) {
    conditionalSteps.push(
      { element: '#genes-dialog #status-choice-container', popover: { title: 'Status', description: 'Select the status of the reptiles you are adding (available, not for sale, sold).' } },
    )
  }

  const driverObj = driver({
    steps: [
      ...conditionalSteps,
      { element: '#genes-dialog #quantity-add-genes', popover: { title: 'Quantity', description: 'Specify how many reptiles you are adding.' } },
      { element: '#genes-dialog #genes-dialog-container', popover: { title: 'Genes', description: 'Select the genes of the reptiles you are adding.' } },
      { element: '#genes-dialog #add-another-gene', popover: { title: 'Add Additional Genes', description: 'Tap this button if you need more genes to specify your reptile. If you can\'t find the genes you are looking for please visit the \'Browse Morphs\' section from the home page. Then select the \'Suggest Gene Button\' and type specify the gene you are looking for. A RepTool moderator will add it to the list. If you need help please email contact@coldbloodedstudios.com.' } },
      { element: '#add-to-collection', popover: { title: 'Add To Collection', description: 'Add the reptiles to your collection.' } },
    ]

  });

  return (
    <ReptoolModal id={"genes-dialog"} title={"Add Animal by Genes"} closeModal={closeModal} showTutorial={() => driverObj.drive()} body={(
      <div>
        {!lockStatus && <div id="status-choice-container">
          <select className='w-100' defaultValue={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)} >
            {tabs.map((tab) => (
              <option key={tab.status} value={tab.status}>{tab.label}</option>
            ))}
          </select>
        </div>}
        <div className="w-100 no-pad my-3 label-title px-2 py-1">
          <h5>How Many Do You Want To Add?</h5>
        </div>
        <SexInputs setValues={(values) => setGenderQuantities(values)} />

        <div className='d-flex flex-row my-4'>
          <Form.Check id='normal-check' className='mt-1' checked={isNormal} onChange={() => setIsNormal(!isNormal)} />
          <label htmlFor='normal-check' className='ml-4 mb-0'>Normal Type (animals have no genes)</label>
        </div>

        {!isNormal && <>
          <div id="genes-dialog-container">
            {selectedGenes.map((gene, index) => {
              let selectableExpressions = geneExpressions;
              if (gene.gene_type == "Recessive") {
                selectableExpressions = selectableExpressions.filter(expression => expression.value != "SUPER")
              } else if (gene.gene_type == "Codom" || gene.gene_type == "Dominant") {
                selectableExpressions = selectableExpressions.filter(expression => expression.value == "SUPER" || expression.value == "NORMAL");
              } else {
                selectableExpressions = geneExpressions.filter(expression => expression.value == "NORMAL");
              }
              return (<div className='mt-3' key={index}>
                <GeneInput
                  id={`genes-${index}`}
                  genes={genes.filter(fGene => selectedGenes.find(selectedGene => selectedGene.gene_name == fGene.gene_name) == undefined || gene.gene_name == fGene.gene_name)}
                  gene={gene}
                  setGene={(geneData) => {
                    const updateGenes = [...selectedGenes];

                    updateGenes[index] = { ...geneData, isHet: geneData.expression != "NORMAL", gene_id: geneData.id };
                    setSelectedGenes(updateGenes);
                  }}
                />
              </div>
              )
            })}
          </div>
          <span id="add-another-gene" onClick={() => setSelectedGenes([...selectedGenes, blankGene])}>
            <FontAwesomeIcon icon={faPlus} /> Add Additional Gene
          </span>
        </>}

        <hr />
        <div className='pb-3'>
          {morphNameString && <div className='text-center d-flex flex-column pb-5'>
            <h5>Preview</h5>
            <div className='d-flex justify-content-center'>
              <CollectionGroup collectionGroup={{
                id: -1,
                reptile_group_id: -1,
                morph_name: morphNameString,
                males: genderQuantities.males,
                females: genderQuantities.females,
                unsexed: genderQuantities.unsexed,
                genes: selectedGenes,
              }} />
            </div>
          </div>}
        </div>
      </div >
    )} />
  )

}